// InviteDialog — 部屋への「招待」(URL 共有) ダイアログ (2026-10-01、room-invite-2026-10-01.md)。
//
// 発端: 「部屋の URL がどこにあるかわからない」FB。アドレスバーしか手段が無く、タブレット /
// ホーム画面アプリでは見えない。部屋 URL を表示 + コピー + 共有シート + QR で渡せるようにする。
//
// - URL は呼び出し側が `buildRoomInviteUrl` で部屋 ID から組んで渡す (query 混入を避ける)。
// - コピー: Clipboard API。成功で「コピーしました」、失敗 / 非対応なら入力欄を全選択して
//   手動コピーを促す (フォールバック)。
// - 共有: `navigator.share` がある環境だけボタンを出す。
// - 計装: copy 成功 / share resolve を `onAction` で通知 (AbortError は送らない)。
// - QR: `qrcode-generator` をダイアログを開いたときに動的 import (初期バンドルに含めない)。
// - あいことば付き部屋: URL に含めない旨の注意書き。
// - ゴーストクリック対策 (oekaki/CLAUDE.md): ヘッダ / 下部バーのボタンは pointerup 起動なので、
//   開いた直後の touch 互換 click が backdrop に着弾して即閉じる。ConfirmDialog と同じ
//   armed ガード (`shouldAllowConfirmDialogClick` / `resetArmedOnReopen`) を流用する。

import type { InviteAction } from '@oekaki/protocol';
import { useEffect, useId, useRef, useState } from 'react';

import { resetArmedOnReopen, shouldAllowConfirmDialogClick } from './ConfirmDialog.js';

export interface InviteDialogProps {
  readonly open: boolean;
  readonly onClose: () => void;
  /** 招待する部屋 URL (`buildRoomInviteUrl` で組んだ正規形)。 */
  readonly url: string;
  readonly roomLabel: string;
  /** true のとき「あいことばは別に伝えてね」を出す。null / false は出さない。 */
  readonly entryPasswordRequired: boolean | null;
  /** 利用計装 (`analytics.invite`)。copy 成功 / Web Share resolve のときに呼ぶ (open は開く側)。 */
  readonly onAction?: (action: InviteAction) => void;
}

type CopyState = 'idle' | 'copied' | 'failed';

/** QR を動的 import で生成して data URL を返す (失敗時は null)。 */
const generateQrDataUrl = async (text: string): Promise<string | null> => {
  try {
    const mod = await import('qrcode-generator');
    const qr = mod.default(0, 'M');
    qr.addData(text);
    qr.make();
    return qr.createDataURL(6, 4);
  } catch {
    return null;
  }
};

export const InviteDialog = ({
  open,
  onClose,
  url,
  roomLabel,
  entryPasswordRequired,
  onAction,
}: InviteDialogProps): React.ReactNode => {
  const titleId = useId();
  const armedRef = useRef(false);
  const prevOpenRef = useRef(open);
  resetArmedOnReopen(armedRef, prevOpenRef, open);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [qrUrl, setQrUrl] = useState<string | null>(null);

  // Esc で閉じる (ConfirmDialog と同流儀、ガード対象外)。
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  // 開いたときだけ QR を生成 (動的 import)。閉じたら state を戻す。
  useEffect(() => {
    if (!open) {
      setCopyState('idle');
      return;
    }
    let cancelled = false;
    void generateQrDataUrl(url).then((dataUrl) => {
      if (!cancelled) setQrUrl(dataUrl);
    });
    return () => {
      cancelled = true;
    };
  }, [open, url]);

  if (!open) return null;

  const guard = (e: { detail: number }): boolean =>
    shouldAllowConfirmDialogClick(armedRef.current, e.detail);

  const selectUrl = (): void => {
    inputRef.current?.focus();
    inputRef.current?.select();
  };

  const handleCopy = async (e: { detail: number }): Promise<void> => {
    if (!guard(e)) return;
    try {
      if (navigator.clipboard === undefined) throw new Error('no clipboard');
      await navigator.clipboard.writeText(url);
      setCopyState('copied');
      onAction?.('copy');
    } catch {
      // 失敗 (権限 / 非対応 / 非 secure context): 選択状態にして手動コピーを促す。
      setCopyState('failed');
      selectUrl();
    }
  };

  const canShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';
  const handleShare = async (e: { detail: number }): Promise<void> => {
    if (!guard(e)) return;
    try {
      await navigator.share({ title: roomLabel, text: `${roomLabel} にお絵かきしよう`, url });
      onAction?.('share');
    } catch {
      // ユーザがシートを閉じた (AbortError) 等。何もしない。
    }
  };

  const btn =
    'rounded border border-white/30 bg-[var(--color-bg-soft)] px-4 py-2 text-sm text-white hover:bg-[var(--color-bg)]';

  return (
    <div
      className="fixed inset-0 z-[60] grid place-items-center p-4"
      onPointerDown={() => {
        armedRef.current = true;
      }}
    >
      <button
        type="button"
        aria-label="閉じる"
        tabIndex={-1}
        onClick={(e) => {
          if (guard(e)) onClose();
        }}
        className="-z-10 absolute inset-0 cursor-default bg-black/60"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="max-h-[90vh] w-full min-w-0 max-w-[400px] overflow-y-auto rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl"
      >
        <h2 id={titleId} className="mb-3 text-base font-bold text-[var(--color-accent)]">
          友達を招待
        </h2>
        <p className="mb-2 text-xs text-[var(--color-text-muted)]">
          この URL を友達に送ると、同じ部屋に入れます。
        </p>
        <input
          ref={inputRef}
          type="text"
          readOnly
          value={url}
          aria-label="部屋の URL"
          onFocus={(e) => e.currentTarget.select()}
          className="mb-3 w-full rounded border border-white/20 bg-[var(--color-bg-inset)] px-2 py-2 text-xs text-white"
        />
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button type="button" onClick={(e) => void handleCopy(e)} className={btn}>
            コピー
          </button>
          {canShare ? (
            <button type="button" onClick={(e) => void handleShare(e)} className={btn}>
              共有
            </button>
          ) : null}
          <span role="status" aria-live="polite" className="text-xs text-[var(--color-accent)]">
            {copyState === 'copied' ? 'コピーしました' : null}
            {copyState === 'failed' ? (
              <span className="text-[var(--color-text-muted)]">
                コピーできませんでした。選択した URL を長押しでコピーしてください
              </span>
            ) : null}
          </span>
        </div>
        {qrUrl !== null ? (
          <div className="mb-3 flex flex-col items-center gap-1">
            <img
              src={qrUrl}
              alt="部屋 URL の QR コード"
              width={192}
              height={192}
              className="rounded bg-white"
              style={{ imageRendering: 'pixelated' }}
            />
            <span className="text-[11px] text-[var(--color-text-muted)]">
              近くの友達はカメラで読み取れます
            </span>
          </div>
        ) : null}
        {entryPasswordRequired === true ? (
          <p
            role="note"
            className="mb-3 rounded border border-amber-400/50 bg-amber-400/10 px-2 py-1.5 text-xs text-amber-300"
          >
            この部屋は入室あいことば付きです。あいことばは URL に含まれないので、別に伝えてね。
          </p>
        ) : null}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={(e) => {
              if (guard(e)) onClose();
            }}
            className={btn}
          >
            閉じる
          </button>
        </div>
      </div>
    </div>
  );
};
